import { useTranslate } from '@tolgee/react';
import React, { FC, useEffect, useRef, useState } from 'react';
import { Button, Dropdown } from 'react-bootstrap';

import { useDispatch, useSelector } from 'react-redux';
import { lomavisCreatorRootState } from '../../../../../pages/lomavisCreator';

import SuccessToast from '../../../cards/toast';
import { AddImageButton, EditorHeader } from '../../sharedComponents';
import toast from 'react-hot-toast';
import { IMAGE, MAX_MEDIA_FILES, VIDEO } from '../../../config/constants';
import axiosClient from '../../../../../utils/axios';
import {
  addMediaFiles,
  getAiTextForBrowserExtention,
  setMediaDataAttribute,
  setModalUiContent
} from '../../../../../redux/slices/lomavisCreator/lomavisCreator';
import { lomavisShowToastr } from '../../../../../utils/ui/toastr';
import EditorTitle from '../../commonElements/editorTitle';
import { relocatePublishButtons } from '../../../utils/uploadMediaFile';
import CreatorTitle from '../../commonElements/creatorTitle';
import PluginMedia from './components/pluginMedia';
import {
  getMediaInfo,
  uploadExternalMediaFiles
} from '../../../../../redux/slices/bulkPostsImport/helpers';
import Delta from 'quill-delta';
import Quill from 'quill';

import { ALL } from '../../../../../config/constants';
import {
  handleBrowserPluginFileChange,
  handleSingleExternalFileUpload
} from '../../MediaUploadArea/utils';
import { AnyAction, ThunkDispatch } from '@reduxjs/toolkit';
import {
  highlightQuillInput,
  trimTrailingNewline
} from '../../creatorTextEditor/config';
import { getLanguage } from '../../../../../locales/language';

interface Props {
  handleClose: Function;
  handleCloseNoConf: Function;
}

const BrowserPluginModal: FC<Props> = ({ handleClose, handleCloseNoConf }) => {
  const { t } = useTranslate();
  const dispatch = useDispatch<ThunkDispatch<any, any, AnyAction>>();
  const footerRef = useRef(null);

  const [rightMargin, setRightMargin] = useState(0);
  const [startSubmission, setStartSubmission] = useState(false);
  const [showPlatformDropdown, setShowPlatformDropdown] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [refetchKey, setRefetchKey] = useState(0);

  const triggerSuccessToast = (type) => {
    toast.success(
      type === IMAGE
        ? t('lomavisCreator.image.saved')
        : t('lomavisCreator.video.saved'),
      {
        position: 'top-right',
        id: 'accounts.invites.table.invite_deleted'
      }
    );
  };

  const lomavisCreator = useSelector(
    (state: lomavisCreatorRootState) => state.lomavisCreator
  );

  const { browserPlugin } = useSelector(
    (state: lomavisCreatorRootState) => state.lomavisCreator.modalStates
  );

  const postDataMediaFiles = useSelector(
    (state: lomavisCreatorRootState) =>
      state.lomavisCreator.postData?.[ALL]?.media
  );

  const typeKey = useSelector(
    (state: lomavisCreatorRootState) =>
      state.lomavisCreator.postTypeSelected[ALL]
  );
  const typeKeyAll = useSelector(
    (state: lomavisCreatorRootState) => state.lomavisCreator.postTypeSelected
  );
  const platformEnabled = useSelector(
    (state: lomavisCreatorRootState) => state.lomavisCreator.platformEnabled
  );
  const { cloudUuid, locationUuid, quillEditors } = useSelector(
    (state: lomavisCreatorRootState) => state.lomavisCreator
  );
  const locationUUID = lomavisCreator.locationUuid;
  const cloudUUID = lomavisCreator.cloudUuid;

  const { image_urls, url, extracted_text } = useSelector(
    (state: lomavisCreatorRootState) =>
      state.lomavisCreator.browserExtensionData
  );

  const mediaFiles = image_urls?.urls;

  const [selectedMedia, setSelectedMedia] = useState([]);

  useEffect(() => {
    relocatePublishButtons({
      footerRef: footerRef,
      setRightMargin: setRightMargin
    });
  }, []);

  useEffect(() => {
    const hasUnsavedChanges = selectedMedia.length > 0;
    if (hasUnsavedChanges && browserPlugin?.allowClose) {
      dispatch(
        setModalUiContent({
          modal: 'browserPlugin',
          attribute: 'allowClose',
          value: false
        })
      );
    } else if (!hasUnsavedChanges && !browserPlugin?.allowClose) {
      dispatch(
        setModalUiContent({
          modal: 'browserPlugin',
          attribute: 'allowClose',
          value: true
        })
      );
    }
  }, [selectedMedia]);

  const [successfulUploadCount, setSuccessfulUploadCount] = useState(0);
  const uploadCountRef = useRef(0);

  const handleSave = async () => {
    if (selectedMedia.length === 0) {
      lomavisShowToastr(
        t('lomavisCreator.pluginMedia.noMediaSelected'),
        'error'
      );
      setStartSubmission(false);
      handleClose();
      return;
    }

    setStartSubmission(true);
    setSuccessfulUploadCount(0); // reset count
    let shouldCloseModal = true;

    try {
      const uploadPromises = selectedMedia.map(async (url) => {
        try {
          const mediaItem = await handleSingleExternalFileUpload(
            url,
            locationUUID,
            cloudUUID
          );

          // Manual immediate count tracking
          uploadCountRef.current += 1;
          setSuccessfulUploadCount(uploadCountRef.current);

          return { success: true, url, mediaItem };
        } catch (error) {
          console.error(`Failed to upload media:`, error);
          lomavisShowToastr(
            t('lomavisCreator.pluginMedia.itmeUploadError'),
            'error'
          );
          return { success: false, url, error };
        }
      });

      const results = await Promise.all(uploadPromises);

      const successfulUploads = results.filter((r) => r.success);

      if (successfulUploads.length === 0) {
        shouldCloseModal = false;
        lomavisShowToastr(
          t('lomavisCreator.pluginMedia.error.noValidUploads'),
          'error'
        );
        return;
      }

      handleBrowserPluginFileChange(
        successfulUploads.map((item) => item.mediaItem),
        dispatch,
        t,
        postDataMediaFiles,
        ALL,
        typeKey,
        typeKeyAll,
        platformEnabled
      );

      dispatch(
        setModalUiContent({
          modal: 'browserPlugin',
          attribute: 'allowClose',
          value: true
        })
      );

      lomavisShowToastr(
        t('lomavisCreator.pluginMedia.success.save'),
        'success'
      );
    } catch (error) {
      shouldCloseModal = false;
      console.error('Save error:', error);
      lomavisShowToastr(t('lomavisCreator.pluginMedia.error.save'), 'error');
    } finally {
      setStartSubmission(false);
      if (shouldCloseModal) {
        handleCloseNoConf(); // ✅ only close if allowed
      }
    }
  };

  return (
    <div className="d-flex flex-column w-100 h-100 gap-4">
      <SuccessToast show={showToast} onClose={() => setShowToast(false)} />

      <EditorHeader>
        <div
          style={{}}
          className="d-flex flex-fill align-items-center h-100 main_editor"
        >
          <EditorTitle title={'mediaBrowserPluginImport'} toolTip={true} />
        </div>
      </EditorHeader>

      {!startSubmission ? (
        <div
          key={refetchKey}
          style={{ overflowY: 'auto' }}
          className="d-flex w-100 flex-column flex-grow-1 flex-fill bg-white rounded-3 p-1"
        >
          <EditorHeader>
            <div
              style={{}}
              className="d-flex justify-content-between align-items-center flex-fill h-100 main_editor"
            >
              <CreatorTitle title={'selectImages'} tooltip={false} />
              <div className="badge bg-primary py-2 px-3">
                {selectedMedia?.length} / {MAX_MEDIA_FILES}{' '}
                {t('lomavisCreator.pluginMedia.selected')}
              </div>
            </div>
          </EditorHeader>

          <PluginMedia
            mediaUrls={mediaFiles}
            selectedMedia={selectedMedia}
            setSelectedMedia={setSelectedMedia}
          />
        </div>
      ) : (
        <div className="d-flex w-100 flex-column flex-grow-1 flex-fill bg-white rounded-3 p-1 justify-content-center align-items-center">
          <div className="d-flex flex-column gap-3 justify-content-center align-items-center">
            <span
              className="spinner-border spinner-border me-2"
              role="status"
              aria-hidden="true"
            ></span>
            <div>
              {successfulUploadCount} / {selectedMedia?.length}
            </div>
            <h3>{t('lomavisCreator.pluginMedia.uploadingMedia')}</h3>
            <p>{t('lomavisCreator.pluginMedia.doNotClose')}</p>
          </div>
        </div>
      )}

      <div
        ref={footerRef}
        style={{ bottom: '0', zIndex: 100 }}
        className="w-100 d-flex justify-content-end gap-3 align-items-center bg-white py-2 border-top h-60px bg-white rounded-3"
      >
        {!startSubmission && (
          <Button
            onClick={() => {
              handleClose();
            }}
            variant="secondary"
            size="sm"
          >
            {t('lomavis_creator.cancel')}
          </Button>
        )}

        <Button
          style={{ marginRight: rightMargin + 'px' }}
          variant="primary"
          size="sm"
          onClick={() => {
            handleSave();
          }}
        >
          {startSubmission && (
            <span
              className="spinner-border spinner-border-sm me-2"
              role="status"
              aria-hidden="true"
            ></span>
          )}
          {startSubmission ? t('lomavis_creator.saving') : t('save')}
        </Button>
      </div>
    </div>
  );
};

export default BrowserPluginModal;
